import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn-in-nx/ui/select";
import { Loader2, RefreshCw, Clock, Building } from "lucide-react";
import HourLogForm from "./HourLogForm";
import HourLogList from "./HourLogList";

const tierLabels = { BASIC_4H: "Starter (4h)", STANDARD_8H: "Professional (8h)", PREMIUM_16H: "Enterprise (16h)" };
const statusColors = { ACTIVE: "bg-green-100 text-green-700", PAUSED: "bg-yellow-100 text-yellow-700", CANCELED: "bg-red-100 text-red-700", PAST_DUE: "bg-orange-100 text-orange-700" };

export default function AdminSupportHours() {
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [statusFilter, setStatusFilter] = useState("ACTIVE");
    const [refreshKey, setRefreshKey] = useState(0);

    const { call: fetchSubs, isLoading } = useBluelagoon({ method: "getAllSupportSubscriptions" });

    const loadSubscriptions = useCallback(async () => {
        try {
            const res = await fetchSubs({ status: statusFilter !== "all" ? statusFilter : undefined, limit: 100 });
            if (res?.data?.subscriptions) setSubscriptions(res.data.subscriptions);
        } catch (err) {
            console.error("Failed to fetch subscriptions:", err);
        }
    }, [fetchSubs, statusFilter]);

    useEffect(() => { loadSubscriptions(); }, [loadSubscriptions]);

    const selected = subscriptions.find(s => s.id === selectedId);

    const onHoursLogged = () => {
        setRefreshKey(k => k + 1);
        loadSubscriptions();
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Support Hours</h1>
                    <p className="text-muted-foreground">Log hours worked and track usage for support subscriptions</p>
                </div>
                <Button onClick={loadSubscriptions} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            <div className="flex gap-4 items-end">
                <div className="space-y-1">
                    <label className="text-sm font-medium">Status</label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="PAUSED">Paused</SelectItem>
                            <SelectItem value="CANCELED">Canceled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1 flex-1 max-w-md">
                    <label className="text-sm font-medium">Subscription</label>
                    <Select value={selectedId} onValueChange={setSelectedId}>
                        <SelectTrigger><SelectValue placeholder={isLoading ? "Loading..." : "Select a subscription"} /></SelectTrigger>
                        <SelectContent>
                            {subscriptions.map(sub => (
                                <SelectItem key={sub.id} value={sub.id}>
                                    {sub.companyName} - {tierLabels[sub.tier] || sub.tier} ({sub.remainingHours}/{sub.totalHours}h)
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {selected && (
                <Card className="p-4">
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{selected.companyName}</span>
                        </div>
                        <Badge className={statusColors[selected.status]}>{selected.status}</Badge>
                        <Badge variant="outline">{tierLabels[selected.tier] || selected.tier}</Badge>
                        <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{selected.remainingHours}h remaining / {selected.totalHours}h total</span>
                        </div>
                    </div>
                </Card>
            )}

            {selected?.status === "ACTIVE" && (
                <HourLogForm subscriptionId={selectedId} remainingHours={selected.remainingHours} onSuccess={onHoursLogged} />
            )}

            {selectedId && <HourLogList key={`logs-${selectedId}-${refreshKey}`} subscriptionId={selectedId} />}
        </div>
    );
}
